import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * `neutral` is for routine confirmations ("Added to Work"): the modal glass,
 * not the green `success` fill, because filing a task is not an achievement.
 */
export type ToastType = 'error' | 'warning' | 'success' | 'neutral';

export interface ToastAction {
  /** Short, already-translated verb ("Show"). */
  label: string;
  onPress: () => void;
}

export interface ToastOptions {
  /** One text action. Non-essential by contract: the toast may time out. */
  action?: ToastAction;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => void;
}

/** Plain toasts. */
const TOAST_MS = 4000;
/** Toasts with an action get longer to reach it (save-to spec §5). */
const ACTION_TOAST_MS = 6000;

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * One toast and its own timer. The timer pauses while the pointer is over the
 * toast or focus is inside it, so reaching for the action never races the
 * dismissal (WCAG 2.2.1), and resumes with the time that was left.
 */
const ToastItem: React.FC<{ toast: Toast; onDismiss: (id: number) => void }> = ({ toast, onDismiss }) => {
  const { id, message, type, action } = toast;
  const remainingRef = useRef(action ? ACTION_TOAST_MS : TOAST_MS);
  const startedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const hoverRef = useRef(false);
  const focusRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current === null) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    remainingRef.current -= Date.now() - startedAtRef.current;
  }, []);

  const start = useCallback(() => {
    if (timerRef.current !== null) return;
    startedAtRef.current = Date.now();
    timerRef.current = window.setTimeout(() => onDismiss(id), Math.max(0, remainingRef.current));
  }, [id, onDismiss]);

  useEffect(() => {
    start();
    return () => {
      // Null the handle too: StrictMode re-runs this effect, and a stale
      // handle would make the second start() think a timer is still running.
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [start]);

  const sync = () => {
    if (hoverRef.current || focusRef.current) clear();
    else start();
  };

  return (
    <div
      className={`toast toast--${type}${action ? ' toast--has-action' : ''}`}
      onMouseEnter={() => { hoverRef.current = true; sync(); }}
      onMouseLeave={() => { hoverRef.current = false; sync(); }}
      onFocus={() => { focusRef.current = true; sync(); }}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        focusRef.current = false;
        sync();
      }}
    >
      <span className="toast-message">{message}</span>
      {action && (
        <button
          type="button"
          className="toast-action tap-target"
          onClick={() => {
            action.onPress();
            onDismiss(id);
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'error', options?: ToastOptions) => {
    const id = ++counter.current;
    setToasts((prev) => [...prev, { id, message, type, action: options?.action }]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Always mounted: a live region must exist BEFORE its content changes,
          or screen readers skip the first message. Empty, it paints nothing
          and takes no pointer events. */}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
